import type { ComponentChildren } from 'preact';
import { useState, useEffect, useRef, useMemo, useCallback } from 'preact/hooks';
import { StackedPromptCards } from './StackedPromptCards';
import { GuestSuccessPanel } from './GuestSuccessPanel';
import { AudioContribution, type AudioContributionResult } from './AudioContribution';
import { VideoContribution, type VideoContributionResult } from './VideoContribution';
import type {
  PromptFlowNext,
  PromptFlowState,
  SubmitResult,
  UnifiedSubmitEntry,
} from '../pages/ContributePage';
import {
  createSmartVideoThumbnail,
  formatTime,
  readMediaMetadata,
} from '../lib/mediaUtils';
import { getDraft } from '../lib/videoDraft';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import styles from './UnifiedComposer.module.css';

const THREAD_TYPE_PLACEHOLDERS: Record<string, string> = {
  memory: 'What do you remember? A detail, a feeling, a moment that stuck with you...',
  legacy: 'Share a memory or a story about this person...',
  event: 'What was it like from your perspective? What stood out?',
};
const DEFAULT_PLACEHOLDER = "What's your take on what happened?";
const LAST_MODE_STORAGE_KEY = 'st-contribution-last-mode';

export interface PromptMeta {
  promptIndex: number;
  promptText: string;
}

interface UnifiedComposerProps {
  onSubmit: (entries: UnifiedSubmitEntry[]) => Promise<SubmitResult>;
  submitting: boolean;
  initialContent?: string;
  threadId: string;
  threadType?: string;
  prompts?: string[];
  promptExamples?: string[];
  promptStates?: Map<number, PromptFlowState>;
  allowedTypes?: Array<'text' | 'photo' | 'audio' | 'video'>;
  visibility: 'public' | 'private';
  onVisibilityChange: (visibility: 'public' | 'private') => void;
  onFocusChange?: (focused: boolean) => void;
  onRequestFullCapture?: (active: boolean) => void;
  onDirtyChange?: (hasUnsentWork: boolean) => void;
  onRequestClose?: () => void;
  /** Success-screen context — the moment the take just joined, and who else is in it. */
  momentTitle?: string | null;
  contributionCount?: number;
  contributorCount?: number;
  contributorNames?: string[];
  /** Navigates to the thread. Without it the chorus CTA is suppressed. */
  onViewChorus?: () => void;
  /** Route to the finished recap, used once the story is over. */
  onViewPayoff?: () => void;
}

type ComposerMode = 'video' | 'audio' | 'text';
type ComposerStep = 'choose' | 'capture' | 'compose';
type MediaType = 'photo' | 'audio' | 'video';

interface MediaAttachmentState {
  file: File | Blob | null;
  type: MediaType | null;
  duration: number | null;
  contentType: string | null;
  filename: string | null;
  thumbnailFile: Blob | null;
  thumbnailFilename: string | null;
  width?: number;
  height?: number;
  waveform: number[];
  coverFrameTimeMs?: number;
  coverSelectionMode?: 'auto' | 'manual';
  audioTrackDegraded?: boolean;
}

interface ModeCardConfig {
  mode: ComposerMode;
  title: string;
  subtitle: string;
  icon: ComponentChildren;
}

function readStoredMode(): ComposerMode | null {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(LAST_MODE_STORAGE_KEY);
  return value === 'video' || value === 'audio' ? value : null;
}

function resolveInitialMode(
  allowedTypes: UnifiedComposerProps['allowedTypes'],
  hasText: boolean,
): ComposerMode {
  const allowed = allowedTypes && allowedTypes.length > 0 ? allowedTypes : undefined;
  const storedMode = readStoredMode();
  if (storedMode === 'video' && (!allowed || allowed.includes('video'))) return 'video';
  if (storedMode === 'audio' && (!allowed || allowed.includes('audio'))) return 'audio';
  if (!allowed || allowed.includes('video')) return 'video';
  if (allowed.includes('audio')) return 'audio';
  return 'text';
}

export function UnifiedComposer({
  onSubmit,
  submitting,
  initialContent = '',
  threadId,
  threadType,
  prompts,
  promptExamples,
  promptStates,
  allowedTypes,
  visibility,
  onVisibilityChange,
  onFocusChange,
  onRequestFullCapture,
  onDirtyChange,
  onRequestClose,
  momentTitle,
  contributionCount,
  contributorCount,
  contributorNames,
  onViewChorus,
  onViewPayoff,
}: UnifiedComposerProps) {
  const hasPrompts = prompts && prompts.length > 0;
  const effectiveAllowedTypes = allowedTypes && allowedTypes.length > 0 ? allowedTypes : undefined;
  const textAllowed = !effectiveAllowedTypes || effectiveAllowedTypes.includes('text');
  const videoAllowed = !effectiveAllowedTypes || effectiveAllowedTypes.includes('video');
  const audioAllowed = !effectiveAllowedTypes || effectiveAllowedTypes.includes('audio');
  const photoAllowed = !effectiveAllowedTypes || effectiveAllowedTypes.includes('photo');

  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number | null>(() => {
    if (!hasPrompts) return null;
    const firstUncovered = prompts.findIndex((_, index) => (promptStates?.get(index) ?? 'unanswered') === 'unanswered');
    return firstUncovered >= 0 ? firstUncovered : 0;
  });
  const [promptTexts, setPromptTexts] = useState<Record<number, string>>({});
  const [freeformText, setFreeformText] = useState(initialContent);
  const [selectedMode, setSelectedMode] = useState<ComposerMode>(() => resolveInitialMode(effectiveAllowedTypes, textAllowed));
  const textOnlyMode = effectiveAllowedTypes && effectiveAllowedTypes.length === 1 && effectiveAllowedTypes[0] === 'text';
  const [composerStep, setComposerStep] = useState<ComposerStep>(() => textOnlyMode ? 'compose' : 'choose');
  const [mediaAttachment, setMediaAttachment] = useState<MediaAttachmentState>({
    file: null,
    type: null,
    duration: null,
    contentType: null,
    filename: null,
    thumbnailFile: null,
    thumbnailFilename: null,
    waveform: [],
    coverFrameTimeMs: undefined,
    coverSelectionMode: undefined,
    audioTrackDegraded: undefined,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [terminalNext, setTerminalNext] = useState<PromptFlowNext | null>(null);
  const [terminalWasQueued, setTerminalWasQueued] = useState(false);
  const [focusSelectedCard, setFocusSelectedCard] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const hasInteracted = useRef(false);
  const advanceTimerRef = useRef<number | null>(null);
  const promptFlowStartedAt = useRef(Date.now());

  const cancelAutoAdvance = useCallback((reason?: string) => {
    if (advanceTimerRef.current != null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
      if (reason) {
        trackCaptureEvent('prompt_auto_advance_cancelled', {
          surface: 'prompt-flow',
          threadId,
          reason,
        });
      }
    }
  }, [threadId]);

  useEffect(() => () => cancelAutoAdvance('close_or_unmount'), [cancelAutoAdvance]);

  useEffect(() => {
    if (initialContent && !freeformText) {
      setFreeformText(initialContent);
    }
  }, [initialContent, freeformText]);

  useEffect(() => {
    if (!hasInteracted.current) return;
    const raf = requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedPromptIndex]);

  useEffect(() => {
    if (!mediaAttachment.file) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(mediaAttachment.file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [mediaAttachment.file]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedMode === 'video' || selectedMode === 'audio') {
      window.localStorage.setItem(LAST_MODE_STORAGE_KEY, selectedMode);
    }
  }, [selectedMode]);

  useEffect(() => {
    onRequestFullCapture?.(composerStep === 'capture' && (selectedMode === 'video' || selectedMode === 'audio'));
  }, [composerStep, selectedMode, onRequestFullCapture]);

  // Auto-navigate to capture step if a video draft exists.
  // Intentionally keyed on threadId only — runs once per thread mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (textOnlyMode) return;
    getDraft(threadId).then(draft => {
      if (draft && composerStep === 'choose') {
        setSelectedMode('video');
        setComposerStep('capture');
      }
    }).catch((err) => console.warn('[UnifiedComposer] getDraft failed:', err));
  }, [threadId]);

  const content = selectedPromptIndex !== null
    ? (promptTexts[selectedPromptIndex] ?? '')
    : freeformText;

  const setContent = (text: string) => {
    cancelAutoAdvance('typing');
    setStatusMessage(null);
    if (selectedPromptIndex !== null) {
      setPromptTexts((current) => ({ ...current, [selectedPromptIndex]: text }));
      return;
    }
    setFreeformText(text);
  };

  const promptResponseEntries = useMemo(() => {
    if (!hasPrompts) {
      return [] as Array<{ promptIndex: number; promptText: string; content: string }>;
    }

    return prompts
      .map((promptText, index) => ({
        promptIndex: index,
        promptText,
        content: (promptTexts[index] ?? '').trim(),
      }))
      .filter((entry) => entry.content.length > 0);
  }, [hasPrompts, promptTexts, prompts]);

  useEffect(() => {
    onDirtyChange?.(
      promptResponseEntries.length > 0
      || freeformText.trim().length > 0
      || mediaAttachment.file !== null,
    );
  }, [promptResponseEntries, freeformText, mediaAttachment.file, onDirtyChange]);

  // Identity of the prompt set is not a proxy for its content: a posted submit fires a
  // fire-and-forget prompts refetch, and the freshly-parsed array is a new identity even
  // when byte-identical. Keying the cancel below on identity would kill the dwell we armed
  // moments earlier on every online submit.
  const promptSetKey = useMemo(() => (prompts ?? []).join('\u0000'), [prompts]);
  const lastPromptSetKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!prompts || prompts.length === 0) return;
    if (lastPromptSetKeyRef.current === promptSetKey) return;
    lastPromptSetKeyRef.current = promptSetKey;
    setSelectedPromptIndex((current) => current == null ? 0 : Math.min(current, prompts.length - 1));
    setPromptTexts((current) => Object.fromEntries(
      Object.entries(current).filter(([index]) => Number(index) < prompts.length),
    ));
    cancelAutoAdvance('prompt_set_changed');
  }, [prompts, promptSetKey, cancelAutoAdvance]);

  const currentFreeformText = selectedPromptIndex === null ? freeformText.trim() : '';
  const storedFreeformDraft = selectedPromptIndex === null ? currentFreeformText : freeformText.trim();
  const totalDraftCount = promptResponseEntries.length + (storedFreeformDraft ? 1 : 0);
  const hasMediaPromptConflict = mediaAttachment.file !== null && totalDraftCount > 1;

  const answeredCount = useMemo(
    () => [...(promptStates?.values() ?? [])].filter((state) => state === 'answered').length,
    [promptStates],
  );
  const queuedCount = useMemo(
    () => [...(promptStates?.values() ?? [])].filter((state) => state === 'queued').length,
    [promptStates],
  );

  const selectedPromptText = selectedPromptIndex !== null && prompts
    ? prompts[selectedPromptIndex]
    : null;

  const placeholder = !textAllowed
    ? 'Add an optional caption...'
    : mediaAttachment.file
      ? 'Add an optional caption...'
      : selectedPromptText
        ? 'Write your response...'
        : threadType
          ? (THREAD_TYPE_PLACEHOLDERS[threadType] ?? DEFAULT_PLACEHOLDER)
          : DEFAULT_PLACEHOLDER;

  const helperMessage = hasMediaPromptConflict
    ? 'Media can only be paired with one response right now.'
    : (!textAllowed && mediaAttachment.file === null)
      ? `This phase accepts ${effectiveAllowedTypes?.filter((type) => type !== 'text').join(', ') ?? 'media'}. Add one to share.`
      : null;

  const hasAnyContent = promptResponseEntries.length > 0 || currentFreeformText.length > 0 || mediaAttachment.file !== null;
  const submitDisabled = submitting || !hasAnyContent || hasMediaPromptConflict || (!textAllowed && mediaAttachment.file === null);

  const buttonText = submitting
    ? 'Posting...'
    : mediaAttachment.type === 'video'
      ? 'Share Video'
      : mediaAttachment.type === 'audio'
        ? 'Share Voice Note'
        : (!mediaAttachment.file && currentFreeformText.length === 0 && promptResponseEntries.length > 1)
          ? `Post ${promptResponseEntries.length} Responses`
          : 'Post';

  const handlePromptChange = useCallback((index: number) => {
    cancelAutoAdvance('manual_navigation');
    setStatusMessage(null);
    setFocusSelectedCard(false);
    hasInteracted.current = true;
    setSelectedPromptIndex(index);
  }, [cancelAutoAdvance]);

  const replaceMedia = (next: Partial<MediaAttachmentState> & { file: File | Blob; type: MediaType }) => {
    setMediaAttachment({
      file: next.file,
      type: next.type,
      duration: next.duration ?? null,
      contentType: next.contentType ?? next.file.type ?? null,
      filename: next.filename ?? null,
      thumbnailFile: next.thumbnailFile ?? null,
      thumbnailFilename: next.thumbnailFilename ?? null,
      width: next.width,
      height: next.height,
      waveform: next.waveform ?? [],
      coverFrameTimeMs: next.coverFrameTimeMs,
      coverSelectionMode: next.coverSelectionMode,
      audioTrackDegraded: next.audioTrackDegraded,
    });
    setComposerStep('compose');
  };

  const clearMedia = (mode: ComposerMode = selectedMode, goToChoose = false) => {
    setMediaAttachment({
      file: null,
      type: null,
      duration: null,
      contentType: null,
      filename: null,
      thumbnailFile: null,
      thumbnailFilename: null,
      waveform: [],
      coverFrameTimeMs: undefined,
      coverSelectionMode: undefined,
      audioTrackDegraded: undefined,
    });
    setSelectedMode(mode);
    if (goToChoose && !textOnlyMode) {
      setComposerStep('choose');
    }
  };

  const handleBackToChoose = () => {
    if (textOnlyMode) return;
    setComposerStep('choose');
  };

  const openPhotoPicker = () => {
    clearMedia(selectedMode, false);
    setComposerStep('compose');
    requestAnimationFrame(() => {
      photoInputRef.current?.click();
    });
  };

  const handlePhotoSelection = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    replaceMedia({
      file,
      type: 'photo',
      contentType: file.type || 'image/jpeg',
      filename: file.name || `photo-${Date.now()}.jpg`,
    });
    input.value = '';
  };

  const handleVideoSelection = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const metadata = await readMediaMetadata(file, 'video');
    const thumbnail = await createSmartVideoThumbnail(file, `video-thumb-${Date.now()}.jpg`);
    replaceMedia({
      file,
      type: 'video',
      duration: metadata.duration,
      contentType: file.type || 'video/mp4',
      filename: file.name || `video-${Date.now()}.mp4`,
      thumbnailFile: thumbnail.blob,
      thumbnailFilename: thumbnail.blob ? thumbnail.filename : null,
      coverFrameTimeMs: thumbnail.frameTimeMs,
      coverSelectionMode: thumbnail.selectionMode,
      width: metadata.width,
      height: metadata.height,
    });
    input.value = '';
  };

  const handleAudioSelection = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const metadata = await readMediaMetadata(file, 'audio');
    replaceMedia({
      file,
      type: 'audio',
      duration: metadata.duration,
      contentType: file.type || 'audio/webm',
      filename: file.name || `voice-${Date.now()}.webm`,
    });
    input.value = '';
  };

  const handleSubmit = async (event: Event) => {
    event.preventDefault();
    if (submitDisabled) return;
    cancelAutoAdvance('new_submit');
    setStatusMessage(null);

    const entries: UnifiedSubmitEntry[] = [];

    if (!mediaAttachment.file && currentFreeformText.length === 0 && promptResponseEntries.length > 1) {
      promptResponseEntries.forEach((entry) => {
        entries.push({
          content: entry.content,
          visibility,
          promptMeta: {
            promptIndex: entry.promptIndex,
            promptText: entry.promptText,
          },
        });
      });
      const result = await onSubmit(entries);
      if (!result || typeof result !== 'object') return;
      handleSubmitResult(result, entries, false);
      return;
    }

    const selectedPromptDraft = selectedPromptIndex !== null && hasPrompts
      ? {
          promptIndex: selectedPromptIndex,
          promptText: prompts[selectedPromptIndex] ?? '',
          content: (promptTexts[selectedPromptIndex] ?? '').trim(),
        }
      : null;
    // A caption-less file belongs to the prompt the user is looking at, so media keeps the
    // selected-prompt attribution even when that prompt is empty. Text has nothing to carry
    // an empty entry: Post is lit by promptResponseEntries, so submitting the empty
    // selection would drop the lone sibling draft and surface a spurious failure.
    const promptEntry = selectedPromptDraft
      && !mediaAttachment.file
      && selectedPromptDraft.content.length === 0
      && promptResponseEntries.length === 1
      ? promptResponseEntries[0]
      : selectedPromptDraft;
    const entry: UnifiedSubmitEntry = {
      content: currentFreeformText || promptEntry?.content || '',
      visibility,
    };

    if (promptEntry) {
      entry.promptMeta = {
        promptIndex: promptEntry.promptIndex,
        promptText: promptEntry.promptText,
      };
    }

    if (mediaAttachment.file && mediaAttachment.type) {
      entry.mediaFile = mediaAttachment.file;
      entry.mediaType = mediaAttachment.type;
      entry.mediaDuration = mediaAttachment.duration ?? undefined;
      entry.mediaContentType = mediaAttachment.contentType ?? undefined;
      entry.mediaFilename = mediaAttachment.filename ?? undefined;
      entry.mediaWidth = mediaAttachment.width;
      entry.mediaHeight = mediaAttachment.height;
      if (mediaAttachment.thumbnailFile) {
        entry.mediaThumbnailFile = mediaAttachment.thumbnailFile;
        entry.mediaThumbnailFilename = mediaAttachment.thumbnailFilename ?? undefined;
      }
      if (mediaAttachment.waveform.length > 0) {
        entry.waveform = mediaAttachment.waveform;
      }
      if (mediaAttachment.coverFrameTimeMs != null) {
        entry.coverFrameTimeMs = mediaAttachment.coverFrameTimeMs;
      }
      if (mediaAttachment.coverSelectionMode) {
        entry.coverSelectionMode = mediaAttachment.coverSelectionMode;
      }
      if (mediaAttachment.audioTrackDegraded != null) {
        entry.audioTrackDegraded = mediaAttachment.audioTrackDegraded;
      }
    }

    const result = await onSubmit([entry]);
    if (!result || typeof result !== 'object') return;
    handleSubmitResult(result, [entry], mediaAttachment.file !== null);
  };

  const handleSubmitResult = (
    result: SubmitResult,
    submittedEntries: UnifiedSubmitEntry[],
    submittedMedia: boolean,
  ) => {
    const medium = submittedEntries.find((entry) => entry.mediaType)?.mediaType ?? 'text';
    trackCaptureEvent('prompt_response_submitted', {
      surface: 'prompt-flow',
      threadId,
      indices: submittedEntries.flatMap((entry) => entry.promptMeta ? [entry.promptMeta.promptIndex] : []),
      count: submittedEntries.length,
      delivery: result.delivery,
      medium,
    });
    if (result.delivery === 'deferred') return;
    if (result.delivery === 'failed') {
      setStatusMessage('That take could not be shared. Your draft is still here.');
      return;
    }

    const clearedIndices = new Set([...result.createdIndices, ...result.queuedIndices]);
    if (clearedIndices.size > 0) {
      setPromptTexts((current) => Object.fromEntries(
        Object.entries(current).filter(([index]) => !clearedIndices.has(Number(index))),
      ));
    }
    const mediaPromptIndex = submittedEntries.find((entry) => entry.mediaFile)?.promptMeta?.promptIndex;
    if (submittedMedia && (mediaPromptIndex == null || clearedIndices.has(mediaPromptIndex))) {
      clearMedia(selectedMode, true);
    }

    if (result.delivery === 'partial') {
      const shared = result.createdCount;
      const failed = result.failedIndices.length;
      setStatusMessage(`${shared} ${shared === 1 ? 'take' : 'takes'} shared · ${failed || 1} couldn't be shared`);
      const firstFailure = result.failedIndices[0];
      if (firstFailure != null) {
        hasInteracted.current = true;
        setSelectedPromptIndex(firstFailure);
      }
      return;
    }

    const next = result.next;
    // `ordinary` used to bail here while ContributePage closed the sheet underneath
    // it — the most common branch, and the only one that acknowledged nothing. It
    // is a terminal state like any other now; only `advance` continues the flow.
    if (!next) return;
    if (next.kind !== 'advance') {
      if (next.kind === 'coverageComplete') {
        trackCaptureEvent('prompt_set_covered', {
          surface: 'prompt-flow',
          threadId,
          count: prompts?.length ?? 0,
          elapsedMs: Date.now() - promptFlowStartedAt.current,
          delivery: result.delivery,
        });
      }
      setTerminalWasQueued(result.delivery === 'queued');
      setTerminalNext(next);
      setStatusMessage(null);
      return;
    }

    setStatusMessage(
      result.delivery === 'queued'
        ? `Saved offline — next up, Prompt ${next.toIndex + 1} of ${prompts?.length ?? 0}`
        : `Take shared — next up, Prompt ${next.toIndex + 1} of ${prompts?.length ?? 0}`,
    );
    const advance = () => {
      advanceTimerRef.current = null;
      hasInteracted.current = true;
      setFocusSelectedCard(submittedMedia);
      setSelectedPromptIndex(next.toIndex);
      if (!submittedMedia) setComposerStep('compose');
    };
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reducedMotion) {
      advance();
    } else {
      advanceTimerRef.current = window.setTimeout(advance, 800);
    }
  };

  const modeCards: ModeCardConfig[] = [];

  if (videoAllowed) {
    modeCards.push({
      mode: 'video',
      title: 'Record Video',
      subtitle: 'Capture a short clip before you write.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      ),
    });
  }

  if (audioAllowed) {
    modeCards.push({
      mode: 'audio',
      title: 'Record Voice',
      subtitle: 'Share a voice note with tone and pacing.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      ),
    });
  }

  if (textAllowed) {
    modeCards.push({
      mode: 'text',
      title: 'Type Instead',
      subtitle: 'Skip straight to a written take.',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M4 7V4h16v3" />
          <path d="M9 20h6" />
          <path d="M12 4v16" />
        </svg>
      ),
    });
  }

  return (
    <form onSubmit={handleSubmit} class={styles.composerForm}>
      {hasPrompts && prompts.length > 1 && !terminalNext && (
        <div class={styles.promptProgress}>
          <span>Prompt {(selectedPromptIndex ?? 0) + 1} of {prompts.length}</span>
          <span>
            {answeredCount} answered
            {queuedCount > 0 ? ` · ${queuedCount} waiting to sync` : ''}
          </span>
          {onRequestClose && (
            <button type="button" class={styles.doneForNow} onClick={() => onRequestClose?.()}>Done for now</button>
          )}
        </div>
      )}

      <div class={styles.statusSlot} role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </div>

      {terminalNext && terminalNext.kind !== 'advance' && (
        <GuestSuccessPanel
          kind={terminalNext.kind}
          queued={terminalWasQueued}
          momentTitle={momentTitle}
          contributionCount={contributionCount}
          contributorCount={contributorCount}
          contributorNames={contributorNames}
          onViewChorus={onViewChorus}
          onViewPayoff={onViewPayoff}
          onDone={() => onRequestClose?.()}
        />
      )}

      {/* ── Step: Choose ── */}
      {!terminalNext && composerStep === 'choose' && (
        <div class={styles.stepChoose}>
          {hasPrompts && (
            <>
              <StackedPromptCards
                prompts={prompts}
                promptExamples={promptExamples}
                selectedIndex={selectedPromptIndex}
                onPromptChange={handlePromptChange}
                promptStates={promptStates}
                focusSelectedCard={focusSelectedCard}
              />
              {prompts.length > 1 && (
                <p class={styles.promptHint}>Swipe the deck to browse prompts, then tap a card to answer it.</p>
              )}
            </>
          )}

          <div class={styles.modeShelf}>
            {modeCards.map((card) => (
              <button
                key={card.mode}
                type="button"
                class={`${styles.modeCard} ${card.mode === 'text' ? styles.modeCardSecondary : styles.modeCardPrimary}`}
                onClick={() => {
                  cancelAutoAdvance('mode_change');
                  setSelectedMode(card.mode);
                  if (card.mode === 'text') {
                    setComposerStep('compose');
                  } else {
                    setComposerStep('capture');
                  }
                }}
              >
                <span class={styles.modeCardIcon}>{card.icon}</span>
                <span class={styles.modeCardMeta}>
                  <span class={styles.modeCardTitle}>{card.title}</span>
                  <span class={styles.modeCardSubtitle}>{card.subtitle}</span>
                </span>
              </button>
            ))}
          </div>

          <div class={styles.secondaryActions}>
            {photoAllowed && (
              <button type="button" class={styles.secondaryActionButton} onClick={openPhotoPicker}>
                Add Photo
              </button>
            )}
            {videoAllowed && (
              <button type="button" class={styles.secondaryActionButton} onClick={() => { setComposerStep('compose'); videoInputRef.current?.click(); }}>
                Choose Existing Video
              </button>
            )}
            {audioAllowed && (
              <button type="button" class={styles.secondaryActionButton} onClick={() => { setComposerStep('compose'); audioInputRef.current?.click(); }}>
                Attach Voice File
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step: Capture ── */}
      {!terminalNext && composerStep === 'capture' && (
        <div class={styles.stepCapture}>
          {selectedMode === 'video' && videoAllowed && (
            <VideoContribution
              threadId={threadId}
              promptText={selectedPromptText ?? undefined}
              onSubmit={(payload: VideoContributionResult) => {
                replaceMedia({
                  file: payload.blob,
                  type: 'video',
                  duration: payload.duration,
                  contentType: payload.contentType,
                  filename: payload.filename,
                  thumbnailFile: payload.thumbnailBlob ?? null,
                  thumbnailFilename: payload.thumbnailFilename ?? null,
                  width: payload.width,
                  height: payload.height,
                  coverFrameTimeMs: payload.coverFrameTimeMs,
                  coverSelectionMode: payload.coverSelectionMode,
                  audioTrackDegraded: payload.audioTrackDegraded,
                });
              }}
              submitting={submitting}
              onUseFallbackCapture={() => { setComposerStep('compose'); videoInputRef.current?.click(); }}
              onBack={handleBackToChoose}
            />
          )}

          {selectedMode === 'audio' && audioAllowed && (
            <AudioContribution
              promptText={selectedPromptText ?? undefined}
              onSubmit={(payload: AudioContributionResult) => {
                replaceMedia({
                  file: payload.blob,
                  type: 'audio',
                  duration: payload.duration,
                  contentType: payload.contentType,
                  filename: payload.filename,
                  waveform: payload.waveform,
                });
              }}
              submitting={submitting}
              heading="Record a voice note"
              submitLabel="Use Recording"
              discardLabel="Record Again"
              onUseFallbackCapture={() => { setComposerStep('compose'); audioInputRef.current?.click(); }}
              onBack={handleBackToChoose}
            />
          )}
        </div>
      )}

      {/* ── Step: Compose ── */}
      {!terminalNext && composerStep === 'compose' && (
        <div class={styles.stepCompose}>
          {!textOnlyMode && (
            <button type="button" class={styles.backButton} onClick={handleBackToChoose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Change format
            </button>
          )}

          {hasPrompts && (
            <>
              <StackedPromptCards
                prompts={prompts}
                promptExamples={promptExamples}
                selectedIndex={selectedPromptIndex}
                onPromptChange={handlePromptChange}
                promptStates={promptStates}
                focusSelectedCard={focusSelectedCard}
              />
              {prompts.length > 1 && (
                <p class={styles.promptHint}>Swipe the deck to browse prompts, then tap a card to answer it.</p>
              )}
            </>
          )}

          <div class={styles.unifiedContentArea}>
            {mediaAttachment.file && mediaAttachment.type && (
              <div class={styles.mediaChip}>
                <span class={styles.mediaChipIcon}>
                  {mediaAttachment.type === 'photo' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                  )}
                  {mediaAttachment.type === 'video' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                  )}
                  {mediaAttachment.type === 'audio' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /></svg>
                  )}
                </span>
                <span class={styles.mediaChipLabel}>
                  {mediaAttachment.type === 'photo'
                    ? 'Photo attached'
                    : mediaAttachment.type === 'video'
                      ? `Video ${mediaAttachment.duration ? formatTime(Math.round(mediaAttachment.duration)) : 'attached'}`
                      : `Voice note ${mediaAttachment.duration ? formatTime(Math.round(mediaAttachment.duration)) : 'attached'}`}
                </span>
                {previewUrl && mediaAttachment.type === 'photo' && (
                  <img src={previewUrl} alt="" class={styles.mediaChipThumb} />
                )}
                <div class={styles.mediaChipActions}>
                  <button
                    type="button"
                    class={styles.mediaChipAction}
                    onClick={() => {
                      if (mediaAttachment.type === 'photo') {
                        openPhotoPicker();
                      } else {
                        clearMedia(mediaAttachment.type === 'audio' ? 'audio' : 'video', false);
                        setComposerStep('capture');
                      }
                    }}
                  >
                    {mediaAttachment.type === 'photo' ? 'Change' : 'Re-record'}
                  </button>
                  <button type="button" class={styles.mediaChipAction} onClick={() => clearMedia(selectedMode, false)}>
                    Remove
                  </button>
                </div>
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={content}
              onInput={(event) => setContent((event.target as HTMLTextAreaElement).value)}
              onFocus={() => onFocusChange?.(true)}
              onBlur={() => onFocusChange?.(false)}
              placeholder={placeholder}
              rows={mediaAttachment.file ? 5 : 8}
              class={styles.textarea}
              aria-label={selectedPromptIndex != null && selectedPromptText
                ? `Your response to Prompt ${selectedPromptIndex + 1}: ${selectedPromptText}`
                : 'Your take'}
              aria-describedby={helperMessage ? 'composer-helper-message' : undefined}
            />

            {helperMessage && <p id="composer-helper-message" class={styles.validationHint}>{helperMessage}</p>}

            <div class={styles.actionToolbar}>
              <div class={styles.toolbarLeft}>
                <div class={styles.visibilityToggle}>
                  <button
                    type="button"
                    class={`${styles.visibilityOption} ${visibility === 'public' ? styles.visibilityActive : ''}`}
                    onClick={() => onVisibilityChange('public')}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    class={`${styles.visibilityOption} ${visibility === 'private' ? styles.visibilityActive : ''}`}
                    onClick={() => onVisibilityChange('private')}
                  >
                    Private
                  </button>
                </div>
              </div>

              <div class={styles.toolbarRight}>
                <button
                  type="submit"
                  disabled={submitDisabled}
                  class={styles.submitButton}
                >
                  {buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        aria-label="Choose photo"
        style={{ display: 'none' }}
        onChange={handlePhotoSelection}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        aria-label="Choose video"
        style={{ display: 'none' }}
        onChange={(event) => { void handleVideoSelection(event); }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        aria-label="Choose audio file"
        style={{ display: 'none' }}
        onChange={(event) => { void handleAudioSelection(event); }}
      />
    </form>
  );
}
